import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { Link } from "@tanstack/react-router";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  Pill,
  SectionHeading,
  SubHeading,
  Chips,
  notify,
  relativeDay,
} from "../components/kit";
import {
  presentLineStatus,
  presentInvoiceStatus,
  presentReceiptStatus,
  TONE_EDGE,
  type StatusTone,
} from "@shared/status";
import { daysBetween } from "@shared/time";
import type { AlertType } from "../../convex/lib/alerts";
import { navFor } from "../lib/nav";
import { useViewer } from "../lib/viewer";

/**
 * Alerts.
 *
 * The owner dashboard says "29 Alerts" and this is where that tap lands, so the
 * screen has about two seconds to answer three questions: what is wrong, how
 * bad, and what do I do about it.
 *
 * Twenty-nine rows in one flat list answers none of them. `convex/alerts.ts`
 * makes the point about deduplication at the point alerts are *raised* — "a
 * list that repeats itself is a list nobody reads" — and the same is true of
 * how they are presented. So: grouped by kind, each group with its own count
 * and its own one-line instruction, oldest first inside the group because the
 * alert nobody has dealt with for three weeks is the one that matters, and only
 * the worst four shown until you ask for the rest.
 *
 * Colours are the client's, not ours. Every live alert is red — red is "this
 * still needs doing" on the sheets they read today — and an acknowledged one
 * settles to gray, which is "dealt with" in the same vocabulary. Green is
 * "ordered" and means nothing here, so it is not used.
 *
 * Authorization is the server's. `alerts.list` refuses assistants outright and
 * hides `low-stock` from anyone who may not see cost; `alerts.acknowledge`
 * refuses assistants and checks the clinic. This component never re-implements
 * any of that — it renders what it is given, offers a filter only for a kind
 * the server has actually returned, and links only to screens the caller's own
 * nav already contains.
 */

/** The server's own list, not a copy of it. A fourth kind added in
 *  `convex/lib/alerts.ts` becomes a type error here — in `TYPE_META`, which is
 *  keyed by it — rather than silently firing into a screen that ignores it. */
type Filter = AlertType | "all" | "done";

interface Alert {
  _id: Id<"alerts">;
  type: string;
  subjectId: string;
  clinicId: Id<"clinics"> | null;
  clinicName: string | null;
  message: string;
  firedAt: number;
  acknowledgedAt: number | null;
}

/**
 * One entry per kind: the label on the pill, the tone, and the sentence that
 * says what to do about it.
 *
 * The tones are read off the shared presenters rather than typed in here, so
 * this screen follows the palette if it ever moves. All three resolve to red
 * today, which is correct — a low-stock item needs ordering, an overdue invoice
 * needs chasing, an unconfirmed delivery needs a phone call. The labels are
 * local because the presenters' own wording belongs to a different domain
 * ("Requested", "Missing") and would read as nonsense on an alert.
 */
interface AlertMeta {
  label: string;
  heading: string;
  instruction: string;
  tone: StatusTone;
}

const TYPE_META: Record<AlertType, AlertMeta> = {
  "low-stock": {
    label: "Low stock",
    heading: "Warehouse below minimum",
    instruction: "Order more from the vendor, then mark it done.",
    tone: presentLineStatus("requested").tone,
  },
  "overdue-invoice": {
    label: "Overdue",
    heading: "Invoices past due",
    instruction: "Chase the clinic's accounting contact, then mark it done.",
    tone: presentInvoiceStatus("overdue").tone,
  },
  "unreceived-dispatch": {
    label: "Not confirmed",
    heading: "Deliveries not confirmed",
    instruction: "Check with the clinic that it arrived, then mark it done.",
    tone: presentReceiptStatus("missing").tone,
  },
};

/** Most disruptive first. A clinic that cannot work beats an unpaid bill. */
const TYPE_ORDER: AlertType[] = [
  "low-stock",
  "overdue-invoice",
  "unreceived-dispatch",
];

const isAlertType = (value: string): value is AlertType => value in TYPE_META;

/**
 * A kind this screen has never heard of still gets read.
 *
 * If someone adds a fourth alert type server-side and forgets this file, the
 * alternative is that it fires correctly and is invisible — which is the exact
 * failure the deduplication comment in `convex/alerts.ts` warns about, arrived
 * at from the other direction: the absence of an alert starts to mean "fine".
 */
const metaFor = (type: string): AlertMeta =>
  isAlertType(type)
    ? TYPE_META[type]
    : {
        label: "Alert",
        heading: "Other alerts",
        instruction: "Deal with it, then mark it done.",
        tone: "red",
      };

/* -------------------------------------------------------------------------- */

export function Alerts() {
  // Remounting the boundary is how a failed query gets retried: Convex throws
  // during render, so there is nothing to re-run without a fresh subtree.
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="Alerts could not load"
      heading={<SectionHeading title="Alerts" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <AlertsScreen />
    </ScreenBoundary>
  );
}

function AlertsScreen() {
  const viewer = useViewer();
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Just-acknowledged rows, kept locally so the row settles in place instead of
   * vanishing out from under the thumb that tapped it. The server has already
   * dropped them from `live` by the time this renders — that is the reactivity
   * working, and it is exactly what makes a bare list feel like a misfire.
   */
  const [settled, setSettled] = useState<Map<string, Alert>>(new Map());

  // Assistants are refused by the server. Skipping the query is a courtesy so
  // they get an explanation instead of an error card — it is not the gate.
  const canQuery = viewer.role !== "assistant";
  /* One subscription for both tabs. `includeAcknowledged` only widens the
     server's filter — same rows, same visibility rule, same sort — so the live
     list is this list with the acknowledged ones dropped, which is a predicate
     this screen already applies below. Two subscriptions meant the Cleared tab
     held the same rows twice. */
  const cleared = useQuery(
    api.alerts.list,
    canQuery ? { includeAcknowledged: true } : "skip",
  );

  const acknowledge = useMutation(api.alerts.acknowledge);

  if (viewer.role === "assistant") {
    return (
      <div data-screen-state="ready" className="space-y-4">
        <SectionHeading title="Alerts" />
        <Empty
          title="Alerts are for managers and the warehouse"
          body="They cover stock levels, unpaid invoices and deliveries nobody confirmed. Your deliveries to confirm are on the Receive screen."
        />
      </div>
    );
  }

  if (cleared === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={4} label="Loading alerts" />
      </div>
    );
  }

  const navPaths = new Set(navFor(viewer.role).map((item) => item.to));
  const showingDone = filter === "done";
  // The two halves of the one list. "Live" is the server's own definition of it:
  // `alerts.list` without `includeAcknowledged` drops exactly these rows.
  const live = cleared.filter((a) => a.acknowledgedAt === null);
  const doneAlerts = cleared.filter((a) => a.acknowledgedAt !== null);

  const clear = async (alert: Alert) => {
    if (busyId) return;
    setBusyId(alert._id);
    try {
      await acknowledge({ alertId: alert._id });
      setSettled((current) => new Map(current).set(alert._id, alert));
    } catch (error) {
      notify.failed("Could not mark that alert done", error);
    } finally {
      setBusyId(null);
    }
  };

  /* The rows for one kind: what the server still considers live, plus anything
     cleared in this sitting, oldest first so the most-ignored is on top. */
  const rowsFor = (type: string): Alert[] => {
    const fromServer = showingDone
      ? doneAlerts.filter((a) => a.type === type)
      : live.filter((a) => a.type === type);
    const local = showingDone
      ? []
      : [...settled.values()].filter((a) => a.type === type);
    return [...fromServer, ...local].sort((a, b) => a.firedAt - b.firedAt);
  };

  const orderedTypes: string[] = [
    ...TYPE_ORDER,
    ...[...new Set([...live, ...doneAlerts].map((a) => a.type))].filter(
      (type) => !isAlertType(type),
    ),
  ];

  const groups = orderedTypes
    .map((type) => ({ type, rows: rowsFor(type) }))
    .filter(
      (group) =>
        group.rows.length > 0 &&
        (filter === "all" || showingDone || filter === group.type),
    );

  const liveCount = live.length;
  const oldest = live.reduce<number | null>(
    (worst, alert) => (worst === null || alert.firedAt < worst ? alert.firedAt : worst),
    null,
  );

  /* Chips only for kinds the server actually returned. A manager is never
     offered a "Low stock" filter, because the server would refuse it and an
     always-empty filter is a broken-looking screen. */
  const typeChips = TYPE_ORDER.filter(
    (type) => live.some((a) => a.type === type) || filter === type,
  ).map((type) => ({
    value: type as Filter,
    label: TYPE_META[type].label,
    count: live.filter((a) => a.type === type).length,
  }));

  const nothingLive = liveCount === 0;

  return (
    <div data-screen-state="ready" className="space-y-4 pb-8">
      <SectionHeading
        title="Alerts"
        subtitle={
          nothingLive
            ? "Nothing needs attention"
            : `${liveCount} need${liveCount === 1 ? "s" : ""} attention${
                oldest !== null ? ` · oldest ${relativeDay(oldest)}` : ""
              }`
        }
      />

      {/* Always rendered, even with nothing live: "Cleared" is how you check
          what was silenced, and that has to stay reachable from the good
          state. */}
      <Chips<Filter>
        value={filter}
        onChange={(value) => {
          setFilter(value);
          setExpanded(new Set());
          // The settled rows belong to the view they were cleared in. On the
          // Cleared tab they come back from the server, and showing both would
          // print the same alert twice.
          if (value === "done") setSettled(new Map());
        }}
        options={[
          { value: "all", label: "All", count: liveCount },
          ...typeChips,
          { value: "done", label: "Cleared" },
        ]}
      />

      {nothingLive && !showingDone ? (
        <Empty
          tone="good"
          title="All clear"
          body="Nothing is under its minimum, no invoice is past due, and every delivery has been confirmed. New alerts appear here the moment they fire."
        />
      ) : null}

      {showingDone && groups.length === 0 ? (
        <Empty
          title="Nothing cleared yet"
          body="Alerts you mark done move here, so it is always possible to see what was silenced and when."
        />
      ) : null}

      {/* Two columns at desktop. Three kinds stacked down the middle of a
          1280px screen is the phone layout stretched; side by side, the whole
          morning's work is visible without scrolling. */}
      <div className="space-y-6 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6 lg:space-y-0">
        {groups.map(({ type, rows }) => {
          const meta = metaFor(type);
          const isExpanded = expanded.has(type);
          const shown = isExpanded ? rows : rows.slice(0, 4);
          const target = linkFor(type, navPaths);
          // The count is what still needs doing, so it drops the moment a row is
          // cleared even though the row itself stays put. A pill that disagrees
          // with the filter chip above it is a pill nobody trusts.
          const outstanding = showingDone
            ? rows.length
            : rows.filter((row) => !settled.has(row._id)).length;

          return (
            <section key={type} className="space-y-2">
              <div className="flex items-center gap-2">
                <SubHeading>{meta.heading}</SubHeading>
                <Pill
                  tone={showingDone || outstanding === 0 ? "gray" : meta.tone}
                  label={outstanding}
                />
                <span aria-hidden className="h-px flex-1 bg-border" />
              </div>
              {/* What this group of alerts wants *done*, not merely that it
                  exists. It was a 12px grey line under the heading and read as
                  a caption; it is the only actionable sentence on the screen,
                  so it now sits in its own band at body size with the verb
                  first. Not red — red is already carrying "this still needs
                  doing" on every row beneath it, and a red instruction on top
                  of red rows is a block of red with no hierarchy. */}
              <p className="rounded-lg border bg-muted/50 px-3 py-2 text-sm leading-snug text-muted-foreground">
                {showingDone ? "Already dealt with." : meta.instruction}
                {target ? (
                  <>
                    {" "}
                    <Link
                      to={target.to}
                      hash={target.hash}
                      className="font-semibold text-primary underline underline-offset-2"
                    >
                      {target.label}
                    </Link>
                  </>
                ) : null}
              </p>

              <div className="space-y-2">
                {shown.map((alert) => (
                  <AlertRow
                    key={alert._id}
                    alert={alert}
                    meta={meta}
                    done={showingDone || settled.has(alert._id)}
                    busy={busyId === alert._id}
                    showClinic={viewer.clinicId === null}
                    onClear={() => clear(alert)}
                  />
                ))}
              </div>

              {rows.length > shown.length ? (
                /* Stock `outline`. The two things it does not already say are
                   the full width and the accent label, which is what makes it
                   read as "there is more" rather than as a second action. */
                <Button
                  variant="outline"
                  onClick={() =>
                    setExpanded((current) => new Set(current).add(type))
                  }
                  className="w-full text-primary"
                >
                  Show all {rows.length}
                </Button>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function AlertRow({
  alert,
  meta,
  done,
  busy,
  showClinic,
  onClear,
}: {
  alert: Alert;
  /** Handed down rather than looked up again: the group this row belongs to has
   *  already resolved it, and one lookup per kind beats one per row. */
  meta: AlertMeta;
  done: boolean;
  busy: boolean;
  showClinic: boolean;
  onClear: () => void;
}) {
  const days = daysBetween(alert.firedAt, Date.now());
  // A null clinic is an org-wide alert — the warehouse shelf, not a clinic.
  const where = alert.clinicName ?? "Warehouse";

  /* A 4px rule rather than a red ring around every card. Twenty-nine
     red-outlined boxes is a wall the eye slides off; the edge keeps red
     saying "this one still needs doing" while the row stays readable. A
     cleared row draws the border transparent rather than dropping it, so the
     row does not shift four pixels as it settles — which is what
     `TONE_EDGE.gray` is. */
  const edge = done ? TONE_EDGE.gray : TONE_EDGE[meta.tone];

  return (
    <Card
      className={cn(
        "flex-row items-start gap-3 border-l-4 p-3",
        edge,
        done && "bg-muted/50",
      )}
    >
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-sm font-medium",
            done && "text-muted-foreground",
          )}
        >
          {alert.message}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Pill
            tone={done ? "gray" : meta.tone}
            label={done ? "Done" : meta.label}
          />
          <span className="text-xs text-muted-foreground">
            {showClinic || alert.clinicId === null ? `${where} · ` : ""}
            <span
              className={
                !done && days >= 7 ? "font-semibold text-red-800" : undefined
              }
            >
              {relativeDay(alert.firedAt)}
            </span>
          </span>
        </div>
      </div>

      {/* The only action on the screen, so it is the accent. Acknowledging is
          not a destructive step and must not be dressed as one. */}
      {done ? null : (
        <Button onClick={onClear} disabled={busy} className="shrink-0">
          {busy ? "…" : "Mark done"}
        </Button>
      )}
    </Card>
  );
}

/**
 * Where to send someone who wants to act on this kind.
 *
 * Only offered when the route is already in the caller's own nav, so the link
 * can never point at a screen the server will refuse or that this role has no
 * business on. `/inventory` is deliberately absent — it is still a placeholder,
 * and a link to a "not built yet" card is worse than no link.
 */
function linkFor(
  type: string,
  navPaths: Set<string>,
): { to: string; hash?: string; label: string } | null {
  if (type === "overdue-invoice" && navPaths.has("/invoices")) {
    return { to: "/invoices", label: "Open invoices →" };
  }
  if (type === "unreceived-dispatch") {
    if (navPaths.has("/receive")) return { to: "/receive", label: "Open receiving →" };
    if (navPaths.has("/dispatch")) return { to: "/dispatch", label: "Open dispatch →" };
  }
  if (type === "low-stock" && navPaths.has("/reports")) {
    return { to: "/reports", hash: "low-stock", label: "Low-stock report →" };
  }
  return null;
}
